// https://github.com/athletic-greens/website/blob/c6d824e38e0ec0e3ec30bf32366f2dd1e21cb5c3/utils/config.js#L106
import { StoreCode, StoreEnum } from 'schema/storeCode';
import { getKeys } from 'shared/utils/getKeys';

type Product = Readonly<{
  oneTime: number;
  subscription: number;
  subscriptionDouble: number;
}>;
export type ProductThreeMonthVariant = Readonly<{
  oneTime: number;
  subscription: number;
  subscriptionDouble: number;
  threeMonthPouch: number;
}>;

export type ProductFamilyPlanVariant = Readonly<{
  oneTime: number;
  subscription: number;
  subscriptionDouble: number;
  familyPlanPouch: number;
}>;

export type ProductVariantType = keyof Product;

type OfferType = 'ultimate-daily' | 'ultimate-daily-travel-packs-30-count';

export const productOfferTypeShort = {
  pouch: 'pouch',
  packs: 'packs'
} as const;

type LegacyProduct = Readonly<
  Record<
    OfferType,
    Readonly<Record<StoreCode, Product | ProductThreeMonthVariant | ProductFamilyPlanVariant>>
  >
>;

type KnownIds = Readonly<Record<StoreCode | 'other', Readonly<number[]>>>;

type PortalCollectionProductsOrder = Readonly<Record<StoreCode, (string | false)[]>>;

type GetProductHandlesOptions = {
  isMetalScoopEnabled?: boolean;
  isShakerEnabled?: boolean;
  isCanisterEnabled?: boolean;
};

export type ExtrasProduct = {
  eligibleProductVariantId: number | number[];
  extraVariantId: number;
  extraQuantity: number;
};

type WelcomeKitTypePouch = 'otpBox' | 'welcomeKit' | 'welcomeKitDouble';
type WelcomeKitTypePouchFamily = 'otpBox' | 'welcomeKit' | 'welcomeKitDouble' | 'welcomeKitFamily';

type WelcomeKitTypePacks = 'otpBox' | 'tpBox' | 'tpBoxDouble';

export type ExtrasProductRecordByOfferType = {
  [productOfferTypeShort.pouch]:
    | Record<WelcomeKitTypePouch, ExtrasProduct>
    | Record<WelcomeKitTypePouchFamily, ExtrasProduct>;
  [productOfferTypeShort.packs]: Record<WelcomeKitTypePacks, ExtrasProduct>;
};

type ExtrasProductRecord = Record<StoreCode, ExtrasProductRecordByOfferType>;

type ExtrasProductFlat = ExtrasProduct & {
  welcomeKitType: WelcomeKitTypePacks | WelcomeKitTypePouch | WelcomeKitTypePouchFamily;
  offerType: keyof typeof productOfferTypeShort;
};

type ExtrasProductsFlat = Record<StoreCode, ExtrasProductFlat[]>;

export const LEGACY_PRODUCTS: LegacyProduct = {
  'ultimate-daily': {
    eu: {
      oneTime: 18948644962373,
      subscription: 18948644896837,
      subscriptionDouble: 31292813639749
    },
    us: {
      oneTime: 39391362187362,
      subscription: 39391362089058,
      subscriptionDouble: 39391362121826,
      threeMonthPouch: 40704528449634,
      familyPlanPouch: 41048012390498
    },
    au: {
      oneTime: 39391362187362,
      subscription: 39391362089058,
      subscriptionDouble: 39391362121826,
      threeMonthPouch: 40704528449634,
      familyPlanPouch: 41048012390498
    },
    nz: {
      oneTime: 39391362187362,
      subscription: 39391362089058,
      subscriptionDouble: 39391362121826,
      threeMonthPouch: 40704528449634,
      familyPlanPouch: 41048012390498
    },
    uk: {
      oneTime: 33149039509581,
      subscription: 33144304074829,
      subscriptionDouble: 33148222832717
    },
    jp: {
      oneTime: 39391362187362,
      subscription: 39391362089058,
      subscriptionDouble: 39391362121826,
      threeMonthPouch: 40704528449634,
      familyPlanPouch: 41048012390498
    },
    qa: {
      oneTime: 40653447921707,
      subscription: 40653447856171,
      subscriptionDouble: 40653447888939,
      threeMonthPouch: 41004249514027,
      familyPlanPouch: 41078604988459
    },
    dev: {
      oneTime: 40653447921707,
      subscription: 40653447856171,
      subscriptionDouble: 40653447888939,
      threeMonthPouch: 41004249514027,
      familyPlanPouch: 41078604988459
    },
    aud: {
      oneTime: 40653447921707,
      subscription: 40653447856171,
      subscriptionDouble: 40653447888939,
      threeMonthPouch: 41004249514027,
      familyPlanPouch: 41078604988459
    },
    nzd: {
      oneTime: 40653447921707,
      subscription: 40653447856171,
      subscriptionDouble: 40653447888939,
      threeMonthPouch: 41004249514027,
      familyPlanPouch: 41078604988459
    },

    eud: {
      oneTime: 45380521066775,
      subscription: 45380521001239,
      subscriptionDouble: 45380521099543
    },
    ukd: {
      oneTime: 45380521066775,
      subscription: 45380521001239,
      subscriptionDouble: 45380521099543
    }
  },
  'ultimate-daily-travel-packs-30-count': {
    eu: {
      oneTime: 18948723245125,
      subscription: 19644890808389,
      subscriptionDouble: 31722788716613
    },
    us: {
      oneTime: 39391367004258,
      subscription: 39391366938722,
      subscriptionDouble: 39391367037026
    },
    au: {
      oneTime: 39391367004258,
      subscription: 39391366938722,
      subscriptionDouble: 39391367037026
    },
    nz: {
      oneTime: 39391367004258,
      subscription: 39391366938722,
      subscriptionDouble: 39391367037026
    },
    uk: {
      oneTime: 33149037674573,
      subscription: 33144536006733,
      subscriptionDouble: 33148224864333
    },
    jp: {
      oneTime: 39391367004258,
      subscription: 39391366938722,
      subscriptionDouble: 39391367037026
    },
    qa: {
      oneTime: 40653448970283,
      subscription: 40653448937515,
      subscriptionDouble: 40653449003051
    },
    dev: {
      oneTime: 40653448970283,
      subscription: 40653448937515,
      subscriptionDouble: 40653449003051
    },
    aud: {
      oneTime: 40653448970283,
      subscription: 40653448937515,
      subscriptionDouble: 40653449003051
    },
    nzd: {
      oneTime: 40653448970283,
      subscription: 40653448937515,
      subscriptionDouble: 40653449003051
    },
    eud: {
      oneTime: 45380520870167,
      subscription: 45380520902935,
      subscriptionDouble: 45380520935703
    },
    ukd: {
      oneTime: 45380520870167,
      subscription: 45380520902935,
      subscriptionDouble: 45380520935703
    }
  }
};

const pouchHandles = [
  'athletic-greens-ultimate-daily',
  'athletic-greens-ultimate-daily-dhit',
  'ultimate-daily-1'
];

const packsHandles = [
  'athletic-greens-ultimate-daily-travel-packs-30-count',
  'athletic-greens-ultimate-daily-travel-packs-30-count-dhit',
  'ultimate-daily-travel-packs-30-count-1'
];

export const freeTravelPackHandles: Readonly<Record<StoreCode, string>> = {
  eu: 'athletic-greens-ultimate-daily-travel-packs-5-count-1',
  us: 'ultimate-daily-travel-packs-5-count-1',
  au: 'ultimate-daily-travel-packs-5-count-1',
  nz: 'ultimate-daily-travel-packs-5-count-1',
  jp: 'ultimate-daily-travel-packs-5-count-1',
  uk: 'athletic-greens-ultimate-daily-travel-packs-5-count',
  qa: 'ultimate-daily-travel-packs-5-count-1',
  dev: 'ultimate-daily-travel-packs-5-count-1',
  aud: 'ultimate-daily-travel-packs-5-count-1',
  nzd: 'ultimate-daily-travel-packs-5-count-1',
  eud: 'athletic-greens-ultimate-daily-travel-packs-5-count-1',
  ukd: 'athletic-greens-ultimate-daily-travel-packs-5-count-1'
};

export const getHandle = (handle: string): string => {
  if (pouchHandles.includes(handle)) {
    return 'ultimate-daily';
  }

  if (packsHandles.includes(handle)) {
    return 'ultimate-daily-travel-packs-30-count';
  }

  return handle;
};

export const KNOWN_POUCH_IDS: KnownIds = {
  eu: [
    LEGACY_PRODUCTS['ultimate-daily'].eu.subscription,
    LEGACY_PRODUCTS['ultimate-daily'].eu.subscriptionDouble
  ],
  us: [
    LEGACY_PRODUCTS['ultimate-daily'].us.subscription,
    LEGACY_PRODUCTS['ultimate-daily'].us.subscriptionDouble,
    (LEGACY_PRODUCTS['ultimate-daily'].us as ProductThreeMonthVariant).threeMonthPouch,
    (LEGACY_PRODUCTS['ultimate-daily'].us as ProductFamilyPlanVariant).familyPlanPouch
  ],
  au: [
    LEGACY_PRODUCTS['ultimate-daily'].au.subscription,
    LEGACY_PRODUCTS['ultimate-daily'].au.subscriptionDouble,
    (LEGACY_PRODUCTS['ultimate-daily'].au as ProductThreeMonthVariant).threeMonthPouch,
    (LEGACY_PRODUCTS['ultimate-daily'].au as ProductFamilyPlanVariant).familyPlanPouch
  ],
  nz: [
    LEGACY_PRODUCTS['ultimate-daily'].nz.subscription,
    LEGACY_PRODUCTS['ultimate-daily'].nz.subscriptionDouble,
    (LEGACY_PRODUCTS['ultimate-daily'].nz as ProductThreeMonthVariant).threeMonthPouch,
    (LEGACY_PRODUCTS['ultimate-daily'].nz as ProductFamilyPlanVariant).familyPlanPouch
  ],
  jp: [],
  uk: [
    LEGACY_PRODUCTS['ultimate-daily'].uk.subscription,
    LEGACY_PRODUCTS['ultimate-daily'].uk.subscriptionDouble
  ],
  qa: [
    LEGACY_PRODUCTS['ultimate-daily'].qa.subscription,
    LEGACY_PRODUCTS['ultimate-daily'].qa.subscriptionDouble,
    (LEGACY_PRODUCTS['ultimate-daily'].qa as ProductThreeMonthVariant).threeMonthPouch,
    (LEGACY_PRODUCTS['ultimate-daily'].qa as ProductFamilyPlanVariant).familyPlanPouch
  ],
  dev: [
    LEGACY_PRODUCTS['ultimate-daily'].qa.subscription,
    LEGACY_PRODUCTS['ultimate-daily'].qa.subscriptionDouble,
    (LEGACY_PRODUCTS['ultimate-daily'].qa as ProductThreeMonthVariant).threeMonthPouch,
    (LEGACY_PRODUCTS['ultimate-daily'].qa as ProductFamilyPlanVariant).familyPlanPouch
  ],
  aud: [
    LEGACY_PRODUCTS['ultimate-daily'].aud.subscription,
    LEGACY_PRODUCTS['ultimate-daily'].aud.subscriptionDouble,
    (LEGACY_PRODUCTS['ultimate-daily'].aud as ProductThreeMonthVariant).threeMonthPouch,
    (LEGACY_PRODUCTS['ultimate-daily'].aud as ProductFamilyPlanVariant).familyPlanPouch
  ],
  nzd: [
    LEGACY_PRODUCTS['ultimate-daily'].nzd.subscription,
    LEGACY_PRODUCTS['ultimate-daily'].nzd.subscriptionDouble,
    (LEGACY_PRODUCTS['ultimate-daily'].nzd as ProductThreeMonthVariant).threeMonthPouch,
    (LEGACY_PRODUCTS['ultimate-daily'].nzd as ProductFamilyPlanVariant).familyPlanPouch
  ],
  eud: [
    LEGACY_PRODUCTS['ultimate-daily'].eud.subscription,
    LEGACY_PRODUCTS['ultimate-daily'].eud.subscriptionDouble
  ],
  ukd: [
    LEGACY_PRODUCTS['ultimate-daily'].eud.subscription,
    LEGACY_PRODUCTS['ultimate-daily'].eud.subscriptionDouble
  ],
  other: [
    LEGACY_PRODUCTS['ultimate-daily'].eu.subscription,
    LEGACY_PRODUCTS['ultimate-daily'].eu.subscriptionDouble,
    LEGACY_PRODUCTS['ultimate-daily'].us.subscription,
    LEGACY_PRODUCTS['ultimate-daily'].us.subscriptionDouble,
    19644872589381,
    39779791994978,
    12309269643356,
    12309270134876,
    18948644929605
  ]
};

export const KNOWN_PACKS_IDS: KnownIds = {
  eu: [
    LEGACY_PRODUCTS['ultimate-daily-travel-packs-30-count'].eu.subscription,
    LEGACY_PRODUCTS['ultimate-daily-travel-packs-30-count'].eu.subscriptionDouble
  ],
  us: [
    LEGACY_PRODUCTS['ultimate-daily-travel-packs-30-count'].us.subscription,
    LEGACY_PRODUCTS['ultimate-daily-travel-packs-30-count'].us.subscriptionDouble
  ],
  au: [
    LEGACY_PRODUCTS['ultimate-daily-travel-packs-30-count'].au.subscription,
    LEGACY_PRODUCTS['ultimate-daily-travel-packs-30-count'].au.subscriptionDouble
  ],
  nz: [
    LEGACY_PRODUCTS['ultimate-daily-travel-packs-30-count'].nz.subscription,
    LEGACY_PRODUCTS['ultimate-daily-travel-packs-30-count'].nz.subscriptionDouble
  ],
  jp: [],
  uk: [
    LEGACY_PRODUCTS['ultimate-daily-travel-packs-30-count'].uk.subscription,
    LEGACY_PRODUCTS['ultimate-daily-travel-packs-30-count'].uk.subscriptionDouble
  ],
  qa: [
    LEGACY_PRODUCTS['ultimate-daily-travel-packs-30-count'].qa.subscription,
    LEGACY_PRODUCTS['ultimate-daily-travel-packs-30-count'].qa.subscriptionDouble
  ],
  dev: [
    LEGACY_PRODUCTS['ultimate-daily-travel-packs-30-count'].qa.subscription,
    LEGACY_PRODUCTS['ultimate-daily-travel-packs-30-count'].qa.subscriptionDouble
  ],
  aud: [
    LEGACY_PRODUCTS['ultimate-daily-travel-packs-30-count'].aud.subscription,
    LEGACY_PRODUCTS['ultimate-daily-travel-packs-30-count'].aud.subscriptionDouble
  ],
  nzd: [
    LEGACY_PRODUCTS['ultimate-daily-travel-packs-30-count'].nzd.subscription,
    LEGACY_PRODUCTS['ultimate-daily-travel-packs-30-count'].nzd.subscriptionDouble
  ],
  eud: [
    LEGACY_PRODUCTS['ultimate-daily-travel-packs-30-count'].eud.subscription,
    LEGACY_PRODUCTS['ultimate-daily-travel-packs-30-count'].eud.subscriptionDouble
  ],
  ukd: [
    LEGACY_PRODUCTS['ultimate-daily-travel-packs-30-count'].eud.subscription,
    LEGACY_PRODUCTS['ultimate-daily-travel-packs-30-count'].eud.subscriptionDouble
  ],
  other: [
    18948723179589, 39785669099618, 39945743302754, 39945754116194, 12670425563234, 31822706376802
  ]
};

const METAL_SCOOP_PRODUCT_ID_QA = 7128976916523;
const METAL_SCOOP_PRODUCT_ID = 6854876102754;
const METAL_SCOOP_PRODUCT_ID_EU = 8983224090964;
const METAL_SCOOP_PRODUCT_ID_UK = 7050900734029;
const METAL_SCOOP_PRODUCT_ID_UKD = 9613322977580;

const FREE_SHAKER_VARIANT_ID = 41544786542690;
const FREE_SHAKER_VARIANT_ID_EU = 50841394250068;
const FREE_SHAKER_VARIANT_ID_UK = 54838342189437;
const FREE_SHAKER_VARIANT_ID_QA = 41201983029291;

// these variants have special logic at various points on the site
export const STORE_SPECIFIC_UNIQUE_VARIANTS_MAP: {
  [key in StoreEnum]: {
    [prop: string]: number | undefined;
  };
} = {
  [StoreEnum.qa]: {
    scoop: METAL_SCOOP_PRODUCT_ID_QA,
    pouch: LEGACY_PRODUCTS['ultimate-daily'].qa.subscription,
    doublePouch: LEGACY_PRODUCTS['ultimate-daily'].qa.subscriptionDouble,
    threeMonthPouch:
      'threeMonthPouch' in LEGACY_PRODUCTS['ultimate-daily'].qa
        ? LEGACY_PRODUCTS['ultimate-daily'].qa.threeMonthPouch
        : undefined,
    familyPlanPouch:
      'familyPlanPouch' in LEGACY_PRODUCTS['ultimate-daily'].qa
        ? LEGACY_PRODUCTS['ultimate-daily'].qa.familyPlanPouch
        : undefined,
    shaker: FREE_SHAKER_VARIANT_ID_QA
  },
  [StoreEnum.dev]: {
    scoop: METAL_SCOOP_PRODUCT_ID_QA,
    pouch: LEGACY_PRODUCTS['ultimate-daily'].qa.subscription,
    doublePouch: LEGACY_PRODUCTS['ultimate-daily'].qa.subscriptionDouble,
    threeMonthPouch:
      'threeMonthPouch' in LEGACY_PRODUCTS['ultimate-daily'].qa
        ? LEGACY_PRODUCTS['ultimate-daily'].qa.threeMonthPouch
        : undefined,
    familyPlanPouch:
      'familyPlanPouch' in LEGACY_PRODUCTS['ultimate-daily'].qa
        ? LEGACY_PRODUCTS['ultimate-daily'].qa.familyPlanPouch
        : undefined,
    shaker: FREE_SHAKER_VARIANT_ID_QA
  },
  [StoreEnum.aud]: {
    scoop: METAL_SCOOP_PRODUCT_ID_QA,
    pouch: LEGACY_PRODUCTS['ultimate-daily'].aud.subscription,
    doublePouch: LEGACY_PRODUCTS['ultimate-daily'].aud.subscriptionDouble,
    threeMonthPouch:
      'threeMonthPouch' in LEGACY_PRODUCTS['ultimate-daily'].aud
        ? LEGACY_PRODUCTS['ultimate-daily'].aud.threeMonthPouch
        : undefined,
    familyPlanPouch:
      'familyPlanPouch' in LEGACY_PRODUCTS['ultimate-daily'].aud
        ? LEGACY_PRODUCTS['ultimate-daily'].aud.familyPlanPouch
        : undefined,
    shaker: FREE_SHAKER_VARIANT_ID_QA
  },
  [StoreEnum.nzd]: {
    scoop: METAL_SCOOP_PRODUCT_ID_QA,
    pouch: LEGACY_PRODUCTS['ultimate-daily'].nzd.subscription,
    doublePouch: LEGACY_PRODUCTS['ultimate-daily'].nzd.subscriptionDouble,
    threeMonthPouch:
      'threeMonthPouch' in LEGACY_PRODUCTS['ultimate-daily'].nzd
        ? LEGACY_PRODUCTS['ultimate-daily'].nzd.threeMonthPouch
        : undefined,
    familyPlanPouch:
      'familyPlanPouch' in LEGACY_PRODUCTS['ultimate-daily'].nzd
        ? LEGACY_PRODUCTS['ultimate-daily'].nzd.familyPlanPouch
        : undefined,
    shaker: FREE_SHAKER_VARIANT_ID_QA
  },
  [StoreEnum.us]: {
    scoop: METAL_SCOOP_PRODUCT_ID,
    pouch: LEGACY_PRODUCTS['ultimate-daily'].us.subscription,
    doublePouch: LEGACY_PRODUCTS['ultimate-daily'].us.subscriptionDouble,
    threeMonthPouch:
      'threeMonthPouch' in LEGACY_PRODUCTS['ultimate-daily'].us
        ? LEGACY_PRODUCTS['ultimate-daily'].us.threeMonthPouch
        : undefined,
    familyPlanPouch:
      'familyPlanPouch' in LEGACY_PRODUCTS['ultimate-daily'].us
        ? LEGACY_PRODUCTS['ultimate-daily'].us.familyPlanPouch
        : undefined,
    shaker: FREE_SHAKER_VARIANT_ID
  },
  [StoreEnum.au]: {
    scoop: METAL_SCOOP_PRODUCT_ID,
    pouch: LEGACY_PRODUCTS['ultimate-daily'].au.subscription,
    doublePouch: LEGACY_PRODUCTS['ultimate-daily'].au.subscriptionDouble,
    threeMonthPouch:
      'threeMonthPouch' in LEGACY_PRODUCTS['ultimate-daily'].au
        ? LEGACY_PRODUCTS['ultimate-daily'].au.threeMonthPouch
        : undefined,
    familyPlanPouch:
      'familyPlanPouch' in LEGACY_PRODUCTS['ultimate-daily'].au
        ? LEGACY_PRODUCTS['ultimate-daily'].au.familyPlanPouch
        : undefined,
    shaker: FREE_SHAKER_VARIANT_ID
  },
  [StoreEnum.nz]: {
    scoop: METAL_SCOOP_PRODUCT_ID,
    pouch: LEGACY_PRODUCTS['ultimate-daily'].nz.subscription,
    doublePouch: LEGACY_PRODUCTS['ultimate-daily'].nz.subscriptionDouble,
    threeMonthPouch:
      'threeMonthPouch' in LEGACY_PRODUCTS['ultimate-daily'].nz
        ? LEGACY_PRODUCTS['ultimate-daily'].nz.threeMonthPouch
        : undefined,
    familyPlanPouch:
      'familyPlanPouch' in LEGACY_PRODUCTS['ultimate-daily'].nz
        ? LEGACY_PRODUCTS['ultimate-daily'].nz.familyPlanPouch
        : undefined,
    shaker: FREE_SHAKER_VARIANT_ID
  },
  [StoreEnum.eu]: {
    scoop: METAL_SCOOP_PRODUCT_ID_EU,
    pouch: LEGACY_PRODUCTS['ultimate-daily'].eu.subscription,
    doublePouch: LEGACY_PRODUCTS['ultimate-daily'].eu.subscriptionDouble,
    shaker: FREE_SHAKER_VARIANT_ID_EU
  },
  [StoreEnum.uk]: {
    scoop: METAL_SCOOP_PRODUCT_ID_UK,
    pouch: LEGACY_PRODUCTS['ultimate-daily'].uk.subscription,
    doublePouch: LEGACY_PRODUCTS['ultimate-daily'].uk.subscriptionDouble,
    shaker: FREE_SHAKER_VARIANT_ID_UK
  },
  [StoreEnum.jp]: {},
  [StoreEnum.eud]: {
    scoop: METAL_SCOOP_PRODUCT_ID, // ToDo: change to right product ids for EUD
    pouch: LEGACY_PRODUCTS['ultimate-daily'].eud.subscription,
    doublePouch: LEGACY_PRODUCTS['ultimate-daily'].eud.subscriptionDouble
  },
  [StoreEnum.ukd]: {
    scoop: METAL_SCOOP_PRODUCT_ID_UKD,
    pouch: LEGACY_PRODUCTS['ultimate-daily'].ukd.subscription,
    doublePouch: LEGACY_PRODUCTS['ultimate-daily'].ukd.subscriptionDouble
  }
};

export const getProductHandles = ({
  isMetalScoopEnabled = false,
  isShakerEnabled = false,
  isCanisterEnabled = false
}: GetProductHandlesOptions): PortalCollectionProductsOrder => ({
  eu: [
    isMetalScoopEnabled && 'metal-scoop-boxed-en',
    'athletic-greens-ultimate-daily-travel-packs-5-count-1',
    'athletic-greens-ultimate-daily-travel-packs-30-count',
    'athletic-greens-omega-3-fish-oil-90-count',
    'vitamin-d3-k2-2',
    'athletic-greens-ultimate-daily'
  ],
  us: [
    isShakerEnabled && 'ag1-shaker-1',
    isCanisterEnabled && 'ag1-canister',
    isMetalScoopEnabled && 'test-metal-scoop-0',
    'ultimate-daily-travel-packs-5-count-1',
    'ultimate-daily-travel-packs-30-count-1',
    'omega-3-fish-oil',
    'vitamin-d3-k2',
    'ultimate-daily-1',
    'ag1-canvas-tote',
    'ag1-corduroy-hat',
    'ag1-trucker-hat'
  ],
  au: [
    isShakerEnabled && 'ag1-shaker-1',
    isCanisterEnabled && 'ag1-canister',
    isMetalScoopEnabled && 'test-metal-scoop-0',
    'ultimate-daily-travel-packs-5-count-1',
    'ultimate-daily-travel-packs-30-count-1',
    'omega-3-fish-oil',
    'vitamin-d3-k2',
    'ultimate-daily-1',
    'ag1-canvas-tote',
    'ag1-corduroy-hat',
    'ag1-trucker-hat'
  ],
  nz: [
    isShakerEnabled && 'ag1-shaker-1',
    isCanisterEnabled && 'ag1-canister',
    isMetalScoopEnabled && 'test-metal-scoop-0',
    'ultimate-daily-travel-packs-5-count-1',
    'ultimate-daily-travel-packs-30-count-1',
    'omega-3-fish-oil',
    'vitamin-d3-k2',
    'ultimate-daily-1',
    'ag1-canvas-tote',
    'ag1-corduroy-hat',
    'ag1-trucker-hat'
  ],
  jp: [
    isShakerEnabled && 'ag1-shaker-1',
    isCanisterEnabled && 'ag1-canister',
    isMetalScoopEnabled && 'test-metal-scoop-0',
    'ultimate-daily-travel-packs-5-count-1',
    'ultimate-daily-travel-packs-30-count-1',
    'omega-3-fish-oil',
    'vitamin-d3-k2',
    'ultimate-daily-1',
    'ag1-canvas-tote',
    'ag1-corduroy-hat',
    'ag1-trucker-hat'
  ],
  uk: [
    isMetalScoopEnabled && 'metal-scoop-boxed-en',
    'athletic-greens-ultimate-daily-travel-packs-5-count',
    'athletic-greens-ultimate-daily-travel-packs-30-count-dhit',
    'athletic-greens-omega-3-fish-oil-90-count',
    'vitamin-d3-k2',
    'athletic-greens-ultimate-daily-dhit',
    'ultimate-daily-travel-packs-5-count'
  ],
  qa: [
    isShakerEnabled && 'ag1-shaker-2',
    isCanisterEnabled && 'ag1-canister',
    isMetalScoopEnabled && 'ag1-metal-scoop',
    'ultimate-daily-travel-packs-30-count-1',
    'ch-ag1-trial-box-no-pouch',
    'ultimate-daily-1',
    'ultimate-daily-travel-packs-5-count-1',
    'omega-3-fish-oil',
    'vitamin-d3-k2',
    'ag1-canvas-tote',
    'ag1-corduroy-hat',
    'ag1-trucker-hat'
  ],
  dev: [
    isShakerEnabled && 'ag1-shaker-2',
    isCanisterEnabled && 'ag1-canister',
    isMetalScoopEnabled && 'ag1-metal-scoop',
    'ultimate-daily-travel-packs-30-count-1',
    'ch-ag1-trial-box-no-pouch',
    'ultimate-daily-1',
    'ultimate-daily-travel-packs-5-count-1',
    'omega-3-fish-oil',
    'vitamin-d3-k2',
    'ag1-canvas-tote',
    'ag1-corduroy-hat',
    'ag1-trucker-hat'
  ],
  aud: [
    isShakerEnabled && 'ag1-shaker-2',
    isCanisterEnabled && 'ag1-canister',
    isMetalScoopEnabled && 'ag1-metal-scoop',
    'ultimate-daily-travel-packs-30-count-1',
    'ch-ag1-trial-box-no-pouch',
    'ultimate-daily-1',
    'ultimate-daily-travel-packs-5-count-1',
    'omega-3-fish-oil',
    'vitamin-d3-k2',
    'ag1-canvas-tote',
    'ag1-corduroy-hat',
    'ag1-trucker-hat'
  ],
  nzd: [
    isShakerEnabled && 'ag1-shaker-2',
    isCanisterEnabled && 'ag1-canister',
    isMetalScoopEnabled && 'ag1-metal-scoop',
    'ultimate-daily-travel-packs-30-count-1',
    'ch-ag1-trial-box-no-pouch',
    'ultimate-daily-1',
    'ultimate-daily-travel-packs-5-count-1',
    'omega-3-fish-oil',
    'vitamin-d3-k2',
    'ag1-canvas-tote',
    'ag1-corduroy-hat',
    'ag1-trucker-hat'
  ],
  eud: [
    isMetalScoopEnabled && 'test-metal-scoop-0',
    'athletic-greens-ultimate-daily-travel-packs-5-count-1',
    'athletic-greens-ultimate-daily-travel-packs-30-count',
    'athletic-greens-omega-3-fish-oil-90-count',
    'vitamin-d3-k2-2',
    'athletic-greens-ultimate-daily'
  ],
  ukd: [
    isMetalScoopEnabled && 'test-metal-scoop-0',
    'athletic-greens-ultimate-daily-travel-packs-5-count-1',
    'athletic-greens-ultimate-daily-travel-packs-30-count',
    'athletic-greens-omega-3-fish-oil-90-count',
    'vitamin-d3-k2-2',
    'athletic-greens-ultimate-daily'
  ]
});

export const legacyProductsPouch = LEGACY_PRODUCTS['ultimate-daily'];
export const legacyProductsPacks = LEGACY_PRODUCTS['ultimate-daily-travel-packs-30-count'];

export const VARIANT_BANNER = 'banner';
export const VARIANT_MODAL = 'modal';

export const EXTRAS_PRODUCTS: ExtrasProductRecord = {
  eu: {
    pouch: {
      otpBox: {
        eligibleProductVariantId: legacyProductsPouch.eu.oneTime,
        extraVariantId: 39321883082821,
        extraQuantity: 1
      },
      welcomeKit: {
        eligibleProductVariantId: legacyProductsPouch.eu.subscription,
        extraVariantId: 31967371165765,
        extraQuantity: 1
      },
      welcomeKitDouble: {
        eligibleProductVariantId: legacyProductsPouch.eu.subscriptionDouble,
        extraVariantId: 40133419401285,
        extraQuantity: 1
      }
    },
    packs: {
      otpBox: {
        eligibleProductVariantId: legacyProductsPacks.eu.oneTime,
        extraVariantId: 39321883639877,
        extraQuantity: 1
      },
      tpBox: {
        eligibleProductVariantId: legacyProductsPacks.eu.subscription,
        extraVariantId: 39321883639877,
        extraQuantity: 1
      },
      tpBoxDouble: {
        eligibleProductVariantId: legacyProductsPacks.eu.subscriptionDouble,
        extraVariantId: 46681180963156,
        extraQuantity: 1
      }
    }
  },
  us: {
    pouch: {
      otpBox: {
        eligibleProductVariantId: legacyProductsPouch.us.oneTime,
        extraVariantId: 40335040282722,
        extraQuantity: 1
      },
      welcomeKit: {
        eligibleProductVariantId: [
          legacyProductsPouch.us.subscription,
          (legacyProductsPouch.us as ProductThreeMonthVariant).threeMonthPouch
        ],
        extraVariantId: 31738915225698,
        extraQuantity: 1
      },
      welcomeKitDouble: {
        eligibleProductVariantId: legacyProductsPouch.us.subscriptionDouble,
        extraVariantId: 40335052931170,
        extraQuantity: 1
      },
      welcomeKitFamily: {
        eligibleProductVariantId: [
          (legacyProductsPouch.us as ProductFamilyPlanVariant).familyPlanPouch
        ],
        extraVariantId: 41053714710626,
        extraQuantity: 1
      }
    },
    packs: {
      otpBox: {
        eligibleProductVariantId: legacyProductsPacks.us.oneTime,
        extraVariantId: 40335040643170,
        extraQuantity: 1
      },
      tpBox: {
        eligibleProductVariantId: legacyProductsPacks.us.subscription,
        extraVariantId: 40335040643170,
        extraQuantity: 1
      },
      tpBoxDouble: {
        eligibleProductVariantId: legacyProductsPacks.us.subscriptionDouble,
        extraVariantId: 40335053389922,
        extraQuantity: 1
      }
    }
  },
  au: {
    pouch: {
      otpBox: {
        eligibleProductVariantId: legacyProductsPouch.au.oneTime,
        extraVariantId: 40335040282722,
        extraQuantity: 1
      },
      welcomeKit: {
        eligibleProductVariantId: [
          legacyProductsPouch.au.subscription,
          (legacyProductsPouch.au as ProductThreeMonthVariant).threeMonthPouch
        ],
        extraVariantId: 31738915225698,
        extraQuantity: 1
      },
      welcomeKitDouble: {
        eligibleProductVariantId: legacyProductsPouch.au.subscriptionDouble,
        extraVariantId: 40335052931170,
        extraQuantity: 1
      },
      welcomeKitFamily: {
        eligibleProductVariantId: [
          (legacyProductsPouch.au as ProductFamilyPlanVariant).familyPlanPouch
        ],
        extraVariantId: 41053714710626,
        extraQuantity: 1
      }
    },
    packs: {
      otpBox: {
        eligibleProductVariantId: legacyProductsPacks.au.oneTime,
        extraVariantId: 40335040643170,
        extraQuantity: 1
      },
      tpBox: {
        eligibleProductVariantId: legacyProductsPacks.au.subscription,
        extraVariantId: 40335040643170,
        extraQuantity: 1
      },
      tpBoxDouble: {
        eligibleProductVariantId: legacyProductsPacks.au.subscriptionDouble,
        extraVariantId: 40335053389922,
        extraQuantity: 1
      }
    }
  },
  nz: {
    pouch: {
      otpBox: {
        eligibleProductVariantId: legacyProductsPouch.nz.oneTime,
        extraVariantId: 40335040282722,
        extraQuantity: 1
      },
      welcomeKit: {
        eligibleProductVariantId: [
          legacyProductsPouch.nz.subscription,
          (legacyProductsPouch.nz as ProductThreeMonthVariant).threeMonthPouch
        ],
        extraVariantId: 31738915225698,
        extraQuantity: 1
      },
      welcomeKitDouble: {
        eligibleProductVariantId: legacyProductsPouch.nz.subscriptionDouble,
        extraVariantId: 40335052931170,
        extraQuantity: 1
      },
      welcomeKitFamily: {
        eligibleProductVariantId: [
          (legacyProductsPouch.nz as ProductFamilyPlanVariant).familyPlanPouch
        ],
        extraVariantId: 41053714710626,
        extraQuantity: 1
      }
    },
    packs: {
      otpBox: {
        eligibleProductVariantId: legacyProductsPacks.nz.oneTime,
        extraVariantId: 40335040643170,
        extraQuantity: 1
      },
      tpBox: {
        eligibleProductVariantId: legacyProductsPacks.nz.subscription,
        extraVariantId: 40335040643170,
        extraQuantity: 1
      },
      tpBoxDouble: {
        eligibleProductVariantId: legacyProductsPacks.nz.subscriptionDouble,
        extraVariantId: 40335053389922,
        extraQuantity: 1
      }
    }
  },
  jp: {
    pouch: {
      otpBox: {
        eligibleProductVariantId: legacyProductsPouch.us.oneTime,
        extraVariantId: 40335040282722,
        extraQuantity: 1
      },
      welcomeKit: {
        eligibleProductVariantId: [
          legacyProductsPouch.us.subscription,
          (legacyProductsPouch.us as ProductThreeMonthVariant).threeMonthPouch
        ],
        extraVariantId: 31738915225698,
        extraQuantity: 1
      },
      welcomeKitDouble: {
        eligibleProductVariantId: legacyProductsPouch.us.subscriptionDouble,
        extraVariantId: 40335052931170,
        extraQuantity: 1
      },
      welcomeKitFamily: {
        eligibleProductVariantId: [
          (legacyProductsPouch.us as ProductFamilyPlanVariant).familyPlanPouch
        ],
        extraVariantId: 41053714710626,
        extraQuantity: 1
      }
    },
    packs: {
      otpBox: {
        eligibleProductVariantId: legacyProductsPacks.us.oneTime,
        extraVariantId: 40335040643170,
        extraQuantity: 1
      },
      tpBox: {
        eligibleProductVariantId: legacyProductsPacks.us.subscription,
        extraVariantId: 40335040643170,
        extraQuantity: 1
      },
      tpBoxDouble: {
        eligibleProductVariantId: legacyProductsPacks.us.subscriptionDouble,
        extraVariantId: 40335053389922,
        extraQuantity: 1
      }
    }
  },
  uk: {
    pouch: {
      otpBox: {
        eligibleProductVariantId: legacyProductsPouch.uk.oneTime,
        extraVariantId: 40091057258573,
        extraQuantity: 1
      },
      welcomeKit: {
        eligibleProductVariantId: legacyProductsPouch.uk.subscription,
        extraVariantId: 40091058733133,
        extraQuantity: 1
      },
      welcomeKitDouble: {
        eligibleProductVariantId: legacyProductsPouch.uk.subscriptionDouble,
        extraVariantId: 40091059060813,
        extraQuantity: 1
      }
    },
    packs: {
      otpBox: {
        eligibleProductVariantId: legacyProductsPacks.uk.oneTime,
        extraVariantId: 40091057422413,
        extraQuantity: 1
      },
      tpBox: {
        eligibleProductVariantId: legacyProductsPacks.uk.subscription,
        extraVariantId: 40091057422413,
        extraQuantity: 1
      },
      tpBoxDouble: {
        eligibleProductVariantId: legacyProductsPacks.uk.subscriptionDouble,
        extraVariantId: 40110994489421,
        extraQuantity: 1
      }
    }
  },
  qa: {
    pouch: {
      otpBox: {
        eligibleProductVariantId: legacyProductsPouch.qa.oneTime,
        extraVariantId: 40783699214379,
        extraQuantity: 1
      },
      welcomeKit: {
        eligibleProductVariantId: [
          legacyProductsPouch.qa.subscription,
          (legacyProductsPouch.qa as ProductThreeMonthVariant).threeMonthPouch
        ],
        extraVariantId: 40783700066347,
        extraQuantity: 1
      },
      welcomeKitDouble: {
        eligibleProductVariantId: legacyProductsPouch.qa.subscriptionDouble,
        extraVariantId: 40783700361259,
        extraQuantity: 1
      },
      welcomeKitFamily: {
        eligibleProductVariantId: [
          (legacyProductsPouch.qa as ProductFamilyPlanVariant).familyPlanPouch
        ],
        extraVariantId: 41079543005227,
        extraQuantity: 1
      }
    },
    packs: {
      otpBox: {
        eligibleProductVariantId: legacyProductsPacks.qa.oneTime,
        extraVariantId: 40783699443755,
        extraQuantity: 1
      },
      tpBox: {
        eligibleProductVariantId: legacyProductsPacks.qa.subscription,
        extraVariantId: 40783699443755,
        extraQuantity: 1
      },
      tpBoxDouble: {
        eligibleProductVariantId: legacyProductsPacks.qa.subscriptionDouble,
        extraVariantId: 40783701278763,
        extraQuantity: 1
      }
    }
  },
  dev: {
    pouch: {
      otpBox: {
        eligibleProductVariantId: legacyProductsPouch.qa.oneTime,
        extraVariantId: 40783699214379,
        extraQuantity: 1
      },
      welcomeKit: {
        eligibleProductVariantId: [
          legacyProductsPouch.qa.subscription,
          (legacyProductsPouch.qa as ProductThreeMonthVariant).threeMonthPouch
        ],
        extraVariantId: 40783700066347,
        extraQuantity: 1
      },
      welcomeKitDouble: {
        eligibleProductVariantId: legacyProductsPouch.qa.subscriptionDouble,
        extraVariantId: 40783700361259,
        extraQuantity: 1
      },
      welcomeKitFamily: {
        eligibleProductVariantId: [
          (legacyProductsPouch.qa as ProductFamilyPlanVariant).familyPlanPouch
        ],
        extraVariantId: 41079543005227,
        extraQuantity: 1
      }
    },
    packs: {
      otpBox: {
        eligibleProductVariantId: legacyProductsPacks.qa.oneTime,
        extraVariantId: 40783699443755,
        extraQuantity: 1
      },
      tpBox: {
        eligibleProductVariantId: legacyProductsPacks.qa.subscription,
        extraVariantId: 40783699443755,
        extraQuantity: 1
      },
      tpBoxDouble: {
        eligibleProductVariantId: legacyProductsPacks.qa.subscriptionDouble,
        extraVariantId: 40783701278763,
        extraQuantity: 1
      }
    }
  },
  aud: {
    pouch: {
      otpBox: {
        eligibleProductVariantId: legacyProductsPouch.aud.oneTime,
        extraVariantId: 40783699214379,
        extraQuantity: 1
      },
      welcomeKit: {
        eligibleProductVariantId: [
          legacyProductsPouch.aud.subscription,
          (legacyProductsPouch.aud as ProductThreeMonthVariant).threeMonthPouch
        ],
        extraVariantId: 40783700066347,
        extraQuantity: 1
      },
      welcomeKitDouble: {
        eligibleProductVariantId: legacyProductsPouch.aud.subscriptionDouble,
        extraVariantId: 40783700361259,
        extraQuantity: 1
      },
      welcomeKitFamily: {
        eligibleProductVariantId: [
          (legacyProductsPouch.aud as ProductFamilyPlanVariant).familyPlanPouch
        ],
        extraVariantId: 41079543005227,
        extraQuantity: 1
      }
    },
    packs: {
      otpBox: {
        eligibleProductVariantId: legacyProductsPacks.aud.oneTime,
        extraVariantId: 40783699443755,
        extraQuantity: 1
      },
      tpBox: {
        eligibleProductVariantId: legacyProductsPacks.aud.subscription,
        extraVariantId: 40783699443755,
        extraQuantity: 1
      },
      tpBoxDouble: {
        eligibleProductVariantId: legacyProductsPacks.aud.subscriptionDouble,
        extraVariantId: 40783701278763,
        extraQuantity: 1
      }
    }
  },
  nzd: {
    pouch: {
      otpBox: {
        eligibleProductVariantId: legacyProductsPouch.nzd.oneTime,
        extraVariantId: 40783699214379,
        extraQuantity: 1
      },
      welcomeKit: {
        eligibleProductVariantId: [
          legacyProductsPouch.nzd.subscription,
          (legacyProductsPouch.nzd as ProductThreeMonthVariant).threeMonthPouch
        ],
        extraVariantId: 40783700066347,
        extraQuantity: 1
      },
      welcomeKitDouble: {
        eligibleProductVariantId: legacyProductsPouch.nzd.subscriptionDouble,
        extraVariantId: 40783700361259,
        extraQuantity: 1
      },
      welcomeKitFamily: {
        eligibleProductVariantId: [
          (legacyProductsPouch.nzd as ProductFamilyPlanVariant).familyPlanPouch
        ],
        extraVariantId: 41079543005227,
        extraQuantity: 1
      }
    },
    packs: {
      otpBox: {
        eligibleProductVariantId: legacyProductsPacks.nzd.oneTime,
        extraVariantId: 40783699443755,
        extraQuantity: 1
      },
      tpBox: {
        eligibleProductVariantId: legacyProductsPacks.nzd.subscription,
        extraVariantId: 40783699443755,
        extraQuantity: 1
      },
      tpBoxDouble: {
        eligibleProductVariantId: legacyProductsPacks.nzd.subscriptionDouble,
        extraVariantId: 40783701278763,
        extraQuantity: 1
      }
    }
  },
  eud: {
    pouch: {
      otpBox: {
        eligibleProductVariantId: legacyProductsPouch.eud.oneTime,
        extraVariantId: 45380514054423,
        extraQuantity: 1
      },
      welcomeKit: {
        eligibleProductVariantId: legacyProductsPouch.eud.subscription,
        extraVariantId: 45380514087191,
        extraQuantity: 1
      },
      welcomeKitDouble: {
        eligibleProductVariantId: legacyProductsPouch.eud.subscriptionDouble,
        extraVariantId: 45380513202455,
        extraQuantity: 1
      }
    },
    packs: {
      otpBox: {
        eligibleProductVariantId: legacyProductsPacks.eud.oneTime,
        extraVariantId: 45380514021655,
        extraQuantity: 1
      },
      tpBox: {
        eligibleProductVariantId: legacyProductsPacks.eud.subscription,
        extraVariantId: 45380514021655,
        extraQuantity: 1
      },
      tpBoxDouble: {
        eligibleProductVariantId: legacyProductsPacks.eud.subscriptionDouble,
        extraVariantId: 45519916728599,
        extraQuantity: 1
      }
    }
  },
  ukd: {
    pouch: {
      otpBox: {
        eligibleProductVariantId: legacyProductsPouch.eud.oneTime,
        extraVariantId: 45380514054423,
        extraQuantity: 1
      },
      welcomeKit: {
        eligibleProductVariantId: legacyProductsPouch.eud.subscription,
        extraVariantId: 45380514087191,
        extraQuantity: 1
      },
      welcomeKitDouble: {
        eligibleProductVariantId: legacyProductsPouch.eud.subscriptionDouble,
        extraVariantId: 45380513202455,
        extraQuantity: 1
      }
    },
    packs: {
      otpBox: {
        eligibleProductVariantId: legacyProductsPacks.eud.oneTime,
        extraVariantId: 45380514021655,
        extraQuantity: 1
      },
      tpBox: {
        eligibleProductVariantId: legacyProductsPacks.eud.subscription,
        extraVariantId: 45380514021655,
        extraQuantity: 1
      },
      tpBoxDouble: {
        eligibleProductVariantId: legacyProductsPacks.eud.subscriptionDouble,
        extraVariantId: 45519916728599,
        extraQuantity: 1
      }
    }
  }
};

// flat array of extras products per store with added offerType & welcomeKitType properties
export const EXTRAS_PRODUCTS_FLAT = Object.fromEntries(
  getKeys(EXTRAS_PRODUCTS).map((store) => [
    store,
    Object.entries(EXTRAS_PRODUCTS[store]).flatMap(([offerType, extrasByWelcomeKitType]) =>
      Object.entries(extrasByWelcomeKitType).map(([welcomeKitType, extra]) => ({
        offerType,
        welcomeKitType,
        ...extra
      }))
    )
  ])
) as ExtrasProductsFlat;

export const FREE_SHIPPING_POUCH_IDS: KnownIds = {
  eu: [],
  us: [40704528449634],
  au: [40704528449634],
  nz: [40704528449634],
  uk: [],
  jp: [],
  qa: [41004249514027],
  dev: [41004249514027],
  aud: [41004249514027],
  nzd: [41004249514027],
  eud: [],
  ukd: [],
  other: []
};

export const CUSTOMS_CLEARANCE_FEES = {
  qa: {
    TW: {
      normalizedItems: [
        {
          id: '',
          productID: 7231278809131,
          variantID: 41079579476011,
          imageUrl:
            'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=',
          name: 'Customs Clearance Fee',
          description: '',
          frequency: 30,
          shipsOneTime: false,
          isWelcomeKit: false,
          isFree: false,
          priceLabel: '$25.00',
          quantity: 1
        }
      ],
      items: [
        {
          applied_discounts: [],
          compare_at_price: false,
          discount_allocations: [],
          fulfillment_service: 'manual',
          gift_card: false,
          grams: 0,
          id: '7231278809131',
          image_url:
            'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=',
          in_stock: true,
          key: '7231278809131',
          line_price: '25.00',
          price: '25.00',
          product_id: 7231278809131,
          properties: {
            'AG Custom Name': 'Customs Clearance Fee'
          },
          quantity: 1,
          requires_shipping: false,
          tax_lines: [],
          taxable: false,
          title: 'Customs Clearance Fee',
          variant_id: 41079579476011,
          vendor: 'Athletic Greens'
        }
      ],
      price: 25.0
    }
  },
  us: {
    TW: {
      normalizedItems: [
        {
          id: '',
          productID: 7095394730082,
          variantID: 41053804462178,
          imageUrl:
            'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=',
          name: 'Customs Clearance Fee',
          description: '',
          frequency: 30,
          shipsOneTime: false,
          isWelcomeKit: false,
          isFree: false,
          priceLabel: '$25.00',
          quantity: 1
        }
      ],
      items: [
        {
          applied_discounts: [],
          compare_at_price: false,
          discount_allocations: [],
          fulfillment_service: 'manual',
          gift_card: false,
          grams: 0,
          id: '7095394730082',
          image_url:
            'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=',
          in_stock: true,
          key: '7095394730082',
          line_price: '25.00',
          price: '25.00',
          product_id: 7095394730082,
          properties: {
            'AG Custom Name': 'Customs Clearance Fee'
          },
          quantity: 1,
          requires_shipping: false,
          tax_lines: [],
          taxable: false,
          title: 'Customs Clearance Fee',
          variant_id: 41053804462178,
          vendor: 'Athletic Greens'
        }
      ],
      price: 25.0
    }
  }
};

const APAC_LEGACY_VARIANTS = {
  singlePouch: 'gid://shopify/ProductVariant/41940066697314',
  doublePouch: 'gid://shopify/ProductVariant/41940066631778',
  singlePack: 'gid://shopify/ProductVariant/41940066336866',
  doublePack: 'gid://shopify/ProductVariant/41940066271330'
};

const APAC_ANZ_VARIANTS = {
  singlePouch: 'gid://shopify/ProductVariant/41909790703714',
  doublePouch: 'gid://shopify/ProductVariant/41909790834786',
  singlePack: 'gid://shopify/ProductVariant/41909791424610',
  doublePack: 'gid://shopify/ProductVariant/41909792637026'
};

export const APAC_MIGRATION_MAP = {
  [APAC_LEGACY_VARIANTS.singlePouch]: APAC_ANZ_VARIANTS.singlePouch,
  [APAC_LEGACY_VARIANTS.doublePouch]: APAC_ANZ_VARIANTS.doublePouch,
  [APAC_LEGACY_VARIANTS.singlePack]: APAC_ANZ_VARIANTS.singlePack,
  [APAC_LEGACY_VARIANTS.doublePack]: APAC_ANZ_VARIANTS.doublePack
};
